/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TelephonyDomainsPage.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Main telephony domains management page with data grid and CRUD operations.
----------------------------------------------------------------*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useDataGrid } from '../../hooks/useDataGrid';
import { Globe } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModulePageContainer } from '../../components/layouts';
import { deleteTelephonyDomainById, navigateToTelephonyDomainDuplicate } from './telephonyDomainsActions';
import { useTelephonyDomainsList } from './hooks/useTelephonyDomainsList';
import { useTelephonyDomainsColumns } from './hooks/useTelephonyDomainsColumns';
import { TelephonyDomainsActions } from './components/TelephonyDomainsActions';
import type { Domain } from './types';

export default function TelephonyDomainsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();
  const tenantSlug = user?.tenant?.slug ?? 'main';

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear page title to show tenant logo + name in header
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Hooks
  const { fetchTenants } = useTelephonyDomainsList();
  const { availableColumns, getColumnRenderers } = useTelephonyDomainsColumns();

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchTenants,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 20,
    moduleName: 'telephonyDomains'
  });

  // Handle create domain
  const handleCreateDomain = useCallback(() => {
    navigate('/admin/multi-tenant/domains/new');
  }, [navigate]);

  // Handle edit domain
  const handleEditDomain = useCallback((domain: Domain) => {
    navigate(`/admin/multi-tenant/domains/${domain.uuid ?? domain.id}`);
  }, [navigate]);

  // Handle duplicate domain
  const handleDuplicateDomain = useCallback((domain: Domain) => {
    navigateToTelephonyDomainDuplicate(navigate, domain.uuid ?? domain.id!);
  }, [navigate]);

  // Handle delete domain
  const handleDeleteDomain = useCallback((domain: Domain) => {
    setDomainToDelete(domain);
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!domainToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTelephonyDomainById(domainToDelete.id!, tenantSlug, user?.id);
      setShowDeleteModal(false);
      setDomainToDelete(null);
      showSuccess(t('telephonyDomains.messages.deleteSuccess'));
      refresh();
    } catch (error) {
      showError(error?.message || t('telephonyDomains.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [domainToDelete, tenantSlug, user?.id, t, refresh, showSuccess, showError]);

  // Render actions
  const renderActions = useCallback(
    (domain: Domain) => (
      <TelephonyDomainsActions
        domain={domain}
        onEdit={handleEditDomain}
        onDuplicate={handleDuplicateDomain}
        onDelete={handleDeleteDomain}
      />
    ),
    [handleDeleteDomain, handleDuplicateDomain, handleEditDomain]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Globe className="h-5 w-5 text-primary" />}
            columns={getColumnRenderers(columns)}
            onColumnToggle={handleColumnToggle}
            loading={loading}
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onAdd={handleCreateDomain}
            onEdit={handleEditDomain}
            onDuplicate={handleDuplicateDomain}
            onDelete={handleDeleteDomain}
            title={t('telephonyDomains.domains')}
            addButtonText={t('common.add')}
            searchPlaceholder={t('telephonyDomains.searchDomains')}
            emptyMessage={t('telephonyDomains.noDomainsFound')}
            deleteTitle={t('telephonyDomains.deleteDomainTitle')}
            deleteMessage={item =>
              item?.domainName
                ? t('telephonyDomains.deleteConfirmTemplate', { name: item.domainName })
                : t('telephonyDomains.deleteConfirmMessage')
            }
            renderActions={renderActions}
            translations={{
              items: t('common.items'),
              columns: t('common.columns'),
              noItemsFound: t('common.noItemsFound'),
              showing: t('common.showing'),
              to: t('common.to'),
              of: t('common.of'),
              results: t('common.results'),
              previous: t('common.previous'),
              next: t('common.next'),
              page: t('common.page'),
              deleteItem: t('common.deleteItem'),
              areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
              delete: t('common.delete'),
              cancel: t('common.cancel'),
              allSelected: t('common.allSelected'),
              selected: t('common.selected'),
              searchOptions: t('common.searchOptions'),
              noOptionsFound: t('common.noOptionsFound'),
              selectAll: t('common.selectAll'),
              clearAll: t('common.clearAll')
            }}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('telephonyDomains.deleteDomainTitle')}
            message={
              domainToDelete
                ? t('telephonyDomains.deleteConfirmTemplate', { name: domainToDelete.domainName })
                : ''
            }
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            isLoading={isDeleting}
            type="danger"
          />
        </>
      }
      formView={null}
    />
  );
}
